import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth, ROLES } from "../lib/auth";
import { toast } from "sonner";
import {
  ShieldCheck,
  ShieldAlert,
  Loader2,
  KeyRound,
  Smartphone,
  Copy,
  Check,
  AlertTriangle,
  Lock,
} from "lucide-react";

export default function Perfil() {
  const { user, refreshMe } = useAuth();
  const [enrolling, setEnrolling] = useState(false);
  const [enrollData, setEnrollData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [disabling, setDisabling] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [pwdState, setPwdState] = useState({ current: "", next: "", confirm: "" });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!user) return null;

  const startEnroll = async () => {
    try {
      const { data } = await api.post("/v1/auth/mfa/enroll");
      setEnrollData(data);
      setEnrolling(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al iniciar MFA");
    }
  };

  const verifyEnroll = async () => {
    if (verifyCode.length < 6) return;
    try {
      const { data } = await api.post("/v1/auth/mfa/verify", { code: verifyCode });
      setBackupCodes(data.backup_codes);
      setEnrolling(false);
      setVerifyCode("");
      await refreshMe();
      toast.success("MFA activado correctamente");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Código inválido");
    }
  };

  const disableMfa = async () => {
    try {
      await api.post("/v1/auth/mfa/disable", { code: disableCode });
      setDisabling(false);
      setDisableCode("");
      await refreshMe();
      toast.success("MFA desactivado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Código inválido");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwdState.next !== pwdState.confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setPwdLoading(true);
    try {
      await api.post("/v1/auth/change-password", {
        current_password: pwdState.current,
        new_password: pwdState.next,
      });
      toast.success("Contraseña actualizada. Vuelve a iniciar sesión.");
      setPwdState({ current: "", next: "", confirm: "" });
      setTimeout(() => {
        localStorage.removeItem("afc_token");
        localStorage.removeItem("afc_refresh");
        window.location.href = "/login";
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar contraseña");
    } finally {
      setPwdLoading(false);
    }
  };

  const copyBackup = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl" data-testid="profile-page">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-mono mb-1">Cuenta y seguridad</div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Mi Perfil</h1>
          <p className="text-gray-600 mt-1 text-sm">Gestiona la seguridad de tu cuenta corporativa.</p>
        </div>

        {/* Datos del usuario */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#E0E621] text-[#0A3747] font-bold flex items-center justify-center text-2xl">
              {user.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1">
              <div className="text-xl font-semibold">{user.name}</div>
              <div className="text-sm text-gray-600">{user.email}</div>
              <div className="text-[11px] mt-1 inline-block px-2 py-0.5 rounded bg-[#0A3747] text-[#E0E621] font-mono uppercase tracking-wider">
                {user.role_display || ROLES[user.role] || user.role}
              </div>
            </div>
          </div>
        </div>

        {/* MFA Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-md flex items-center justify-center ${user.mfa_enabled ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                {user.mfa_enabled ? <ShieldCheck size={20} /> : <ShieldAlert size={20} />}
              </div>
              <div>
                <h2 className="font-semibold text-lg">Autenticación en dos pasos (MFA)</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {user.mfa_enabled
                    ? "MFA está activado. Tu cuenta tiene una capa adicional de seguridad."
                    : "Añade una capa extra de seguridad usando una app autenticadora."}
                </p>
              </div>
            </div>
            {!user.mfa_enabled && !enrolling && (
              <button
                onClick={startEnroll}
                className="px-4 py-2 rounded-md bg-[#0A3747] text-white text-sm hover:bg-[#135166] transition flex items-center gap-2"
                data-testid="mfa-enable-btn"
              >
                <Smartphone size={14} /> Activar MFA
              </button>
            )}
            {user.mfa_enabled && !disabling && (
              <button
                onClick={() => setDisabling(true)}
                className="px-4 py-2 rounded-md border border-red-300 text-red-700 text-sm hover:bg-red-50 transition"
                data-testid="mfa-disable-btn"
              >
                Desactivar
              </button>
            )}
          </div>

          {!user.mfa_enabled && !enrolling && (
            <div className="bg-[#FFFBE6] border border-[#F0D88A] rounded-md p-4 text-sm text-[#5C4500] flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <strong>Recomendado para roles Admin y Financiero.</strong> Se necesita una app gratis como
                <em> Google Authenticator</em>, <em>Authy</em> o <em>Microsoft Authenticator</em>.
              </div>
            </div>
          )}

          {enrolling && enrollData && (
            <div className="border-2 border-[#E0E621] rounded-md p-5 bg-[#F8FBE6]" data-testid="mfa-enroll-panel">
              <h3 className="font-semibold mb-3">Paso 1 · Escanea el código QR</h3>
              <div className="flex gap-6 items-start">
                <img src={enrollData.qr_code} alt="QR MFA" className="w-44 h-44 bg-white p-2 rounded border" />
                <div className="flex-1 text-sm">
                  <p className="text-gray-700 mb-2">Abre tu app autenticadora y escanea el código.</p>
                  <p className="text-gray-700 mb-2">¿No puedes escanear? Ingresa esta clave manualmente:</p>
                  <code className="block bg-white p-2 rounded text-xs font-mono break-all border border-gray-200">{enrollData.secret}</code>
                </div>
              </div>
              <div className="mt-5">
                <h3 className="font-semibold mb-2">Paso 2 · Ingresa el código de 6 dígitos</h3>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="px-4 py-2 border-2 border-gray-300 rounded-md text-lg font-mono tracking-widest w-44 text-center focus:border-[#E0E621] outline-none"
                    placeholder="000 000"
                    autoFocus
                    data-testid="mfa-verify-input"
                  />
                  <button
                    onClick={verifyEnroll}
                    disabled={verifyCode.length < 6}
                    className="px-5 py-2 bg-[#0A3747] text-white rounded-md text-sm font-medium hover:bg-[#135166] disabled:opacity-40"
                    data-testid="mfa-verify-btn"
                  >
                    Verificar y activar
                  </button>
                  <button
                    onClick={() => { setEnrolling(false); setEnrollData(null); }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {disabling && (
            <div className="border-2 border-red-300 rounded-md p-5 bg-red-50">
              <h3 className="font-semibold mb-2 text-red-900">Confirmar desactivación</h3>
              <p className="text-sm text-red-800 mb-3">Ingresa un código actual de tu app autenticadora para confirmar.</p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="px-4 py-2 border-2 border-red-300 rounded-md text-lg font-mono tracking-widest w-44 text-center"
                  placeholder="000 000"
                  autoFocus
                  data-testid="mfa-disable-input"
                />
                <button
                  onClick={disableMfa}
                  disabled={disableCode.length < 6}
                  className="px-5 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-40"
                  data-testid="mfa-disable-confirm-btn"
                >
                  Desactivar MFA
                </button>
                <button
                  onClick={() => { setDisabling(false); setDisableCode(""); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-white"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {backupCodes && (
            <div className="border-2 border-green-300 rounded-md p-5 bg-green-50 mt-4" data-testid="backup-codes-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-green-900">Códigos de respaldo · Guárdalos en lugar seguro</h3>
                <button onClick={copyBackup} className="flex items-center gap-1 text-sm text-green-700 hover:text-green-900">
                  {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copiado" : "Copiar"}
                </button>
              </div>
              <p className="text-xs text-green-800 mb-3">
                Si pierdes acceso a tu app autenticadora, usa uno de estos códigos. Cada código sólo se puede usar una vez.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-sm">
                {backupCodes.map((c) => (
                  <div key={c} className="bg-white border border-green-200 rounded px-3 py-2 text-center">{c}</div>
                ))}
              </div>
              <button
                onClick={() => setBackupCodes(null)}
                className="mt-4 text-sm text-green-700 hover:underline"
              >
                Ya los guardé
              </button>
            </div>
          )}
        </div>

        {/* Change password */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center">
              <Lock size={20} />
            </div>
            <div>
              <h2 className="font-semibold text-lg">Cambiar contraseña</h2>
              <p className="text-sm text-gray-600 mt-1">Mínimo 12 caracteres, con mayúsculas, minúsculas, números y símbolos. No puede coincidir con tus últimas 5 contraseñas.</p>
            </div>
          </div>

          <form onSubmit={changePassword} className="space-y-3 max-w-md" data-testid="change-password-form">
            <input
              type="password"
              value={pwdState.current}
              onChange={(e) => setPwdState({ ...pwdState, current: e.target.value })}
              placeholder="Contraseña actual"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
              required
              data-testid="pwd-current-input"
            />
            <input
              type="password"
              value={pwdState.next}
              onChange={(e) => setPwdState({ ...pwdState, next: e.target.value })}
              placeholder="Nueva contraseña"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
              required
              data-testid="pwd-new-input"
            />
            <input
              type="password"
              value={pwdState.confirm}
              onChange={(e) => setPwdState({ ...pwdState, confirm: e.target.value })}
              placeholder="Confirmar nueva contraseña"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
              required
              data-testid="pwd-confirm-input"
            />
            <button
              type="submit"
              disabled={pwdLoading}
              className="px-5 py-2 bg-[#0A3747] text-white rounded-md text-sm font-medium hover:bg-[#135166] disabled:opacity-40 flex items-center gap-2"
              data-testid="pwd-submit-btn"
            >
              {pwdLoading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
              Cambiar contraseña
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
